import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { casesAPI, documentsAPI, tasksAPI, messagesAPI, poaAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Upload, Clock, CheckCircle2, Circle, FileText, Eye, Download, Printer, ExternalLink, Scale, Brain } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import MessageBoard from '../components/MessageBoard';
import KanbanBoard from '../components/KanbanBoard';
import DocumentPreview from '../components/DocumentPreview';
import CaseTaskBoard from '../components/CaseTaskBoard';
import { BlinkingDot } from '../components/UnreadBadge';

const CaseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [caseTasks, setCaseTasks] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [poaData, setPOAData] = useState(null);

  useEffect(() => {
    loadCaseData();
    loadUnreadCount();
    loadCaseTasks();
  }, [id]);

  // Mark messages as read when accessing Messages tab
  useEffect(() => {
    if (activeTab === 'messages' && unreadMessages > 0) {
      markMessagesAsRead();
    }
  }, [activeTab]);

  const loadCaseTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCaseTasks(data);
      }
    } catch (error) {
      console.error('Failed to load case tasks:', error);
    }
  };

  const loadCaseData = async () => {
    try {
      const [caseRes, timelineRes, docsRes, tasksRes, reportRes] = await Promise.all([
        casesAPI.getCase(id),
        casesAPI.getTimeline(id),
        casesAPI.getDocuments(id),
        casesAPI.getTasks(id),
        casesAPI.getReport(id)
      ]);
      
      setCaseData(caseRes.data);
      setTimeline(timelineRes.data);
      setDocuments(docsRes.data);
      setTasks(tasksRes.data);
      setReport(reportRes.data);
      
      // Load POA data
      try {
        const poaRes = await poaAPI.getPOAForCase(id);
        setPOAData(poaRes.data);
      } catch (error) {
        // No POA for this case
        setPOAData(null);
      }
    } catch (error) {
      console.error('Failed to load case data:', error);
      toast.error('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await messagesAPI.getUnreadByCase();
      const unreadByCase = response.data || {};
      setUnreadMessages(unreadByCase[id] || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      await messagesAPI.markAllCaseMessagesRead(id);
      setUnreadMessages(0);
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('case_id', id);
    formData.append('document_type', 'merchant_processing');

    try {
      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully');
      loadCaseData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await tasksAPI.updateStatus(taskId, newStatus);
      toast.success('Task updated');
      loadCaseData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  if (loading) {
    return (
      <div className="app-layout min-h-screen flex items-center justify-center">
        <div className="text-slate-600">Loading case details...</div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="app-layout min-h-screen flex items-center justify-center">
        <div className="text-slate-600">Case not found</div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      in_progress: 'bg-blue-100 text-blue-800',
      under_review: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-slate-100 text-slate-800'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="app-layout min-h-screen">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link
            to={user?.role === 'admin' ? '/admin/dashboard' : '/dashboard'}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </Link>
          <div className="text-xl font-bold text-slate-900">
            Case: {caseData.company_name}
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="dashboard-card mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="case-company-name">
                {caseData.company_name}
              </h1>
              <p className="text-slate-600">Processor: {caseData.processor_name}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(caseData.status)}`}>
              {caseData.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className="text-sm text-slate-600 mb-1">Frozen Amount</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="case-frozen-amount">
                ${caseData.frozen_amount.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-600 mb-1">Freeze Date</div>
              <div className="text-lg font-semibold text-slate-900">
                {format(new Date(caseData.freeze_date), 'MMM dd, yyyy')}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-600 mb-1">Est. Release Date</div>
              <div className="text-lg font-semibold text-slate-900">
                {caseData.estimated_release_date
                  ? format(new Date(caseData.estimated_release_date), 'MMM dd, yyyy')
                  : 'TBD'}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-600 mb-1">Days Frozen</div>
              <div className="text-2xl font-bold text-slate-900">{report?.days_frozen || 0}</div>
            </div>
          </div>

          {report && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm text-slate-600 mb-2">Recovery Progress ({caseTasks.filter(t => t.status === 'done').length}/{caseTasks.length} tasks completed)</div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div
                      className="bg-purple-600 h-3 rounded-full transition-all"
                      style={{ width: `${caseTasks.length > 0 ? Math.round((caseTasks.filter(t => t.status === 'done').length / caseTasks.length) * 100) : report.progress_percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  {caseTasks.length > 0 ? Math.round((caseTasks.filter(t => t.status === 'done').length / caseTasks.length) * 100) : report.progress_percentage}%
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-slate-200">
          <div className="flex gap-6">
            {['overview', 'tasks', 'timeline', 'documents', 'poa', 'messages'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 px-2 border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === tab
                    ? 'border-purple-600 text-purple-600 font-semibold'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'poa' ? 'Power of Attorney' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'messages' && unreadMessages > 0 && activeTab !== 'messages' && (
                  <BlinkingDot active={true} size="sm" />
                )}
                {tab === 'poa' && poaData?.status === 'completed' && (
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                )}
                {tab === 'poa' && poaData?.status === 'pending_admin' && (
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && report && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="dashboard-card">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Case Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Tasks</span>
                  <span className="font-semibold">{report.total_tasks}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Completed Tasks</span>
                  <span className="font-semibold text-green-600">{report.completed_tasks}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Documents Uploaded</span>
                  <span className="font-semibold">{report.documents_uploaded}</span>
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {report.recent_timeline?.slice(0, 3).map((event, idx) => (
                  <div key={idx} className="text-sm">
                    <div className="font-semibold text-slate-900">{event.title}</div>
                    <div className="text-slate-600">{event.description}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {format(new Date(event.event_date), 'MMM dd, yyyy hh:mm a')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="dashboard-card">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Case Timeline</h3>
            
            {/* Task Status in Timeline */}
            {caseTasks && caseTasks.length > 0 && (
              <div className="mb-6 bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-3">Task Progress</h4>
                <div className="space-y-2">
                  {caseTasks.map((task, idx) => (
                    <div key={task.id || idx} className="flex items-center gap-3 text-sm">
                      <span className={`w-3 h-3 rounded-full ${
                        task.status === 'done' ? 'bg-green-500' :
                        task.status === 'in_progress' ? 'bg-blue-500 animate-pulse' :
                        'bg-slate-300'
                      }`}></span>
                      <span className={`flex-1 ${task.status === 'done' ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                        {task.title}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        task.status === 'done' ? 'bg-green-100 text-green-700' :
                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {task.status === 'done' ? 'Completed' : task.status === 'in_progress' ? 'In Progress' : 'To Do'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-6">
              {timeline.map((event, idx) => (
                <div key={event.id} className="timeline-item" data-testid={`timeline-event-${idx}`}>
                  <div className="timeline-dot"></div>
                  <div className="text-sm text-slate-600 mb-1">
                    {format(new Date(event.event_date), 'MMM dd, yyyy hh:mm a')}
                  </div>
                  <div className="font-semibold text-slate-900 mb-1">{event.title}</div>
                  <div className="text-slate-600">{event.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="dashboard-card">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Documents</h3>
                <p className="text-sm text-slate-500">Upload: Merchant Processor Contracts, Processing Statements, Bank Statements, Identity Documents, and other pertinent documents for this case. Auto-backed up to Dropbox.</p>
              </div>
              <label className="primary-button cursor-pointer flex items-center gap-2">
                <Upload size={20} />
                {uploading ? 'Uploading...' : 'Upload Document'}
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="document-upload-input"
                />
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {documents.length === 0 ? (
                <div className="col-span-2 text-center py-12 bg-slate-50 rounded-lg">
                  <FileText className="mx-auto text-slate-400 mb-4" size={48} />
                  <p className="text-slate-600">No documents uploaded yet</p>
                  <p className="text-sm text-slate-400 mt-1">Upload Merchant Processor Contracts, Processing Statements, Bank Statements, Identity Documents, and other pertinent documents</p>
                </div>
              ) : (
                documents.map((doc) => (
                  <DocumentPreview
                    key={doc.id}
                    document={{
                      ...doc,
                      name: doc.filename,
                      url: doc.file_path ? `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download` : null,
                      preview_url: doc.file_path ? `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download` : null
                    }}
                    dropboxUrl={doc.dropbox_url}
                    onDownload={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`, '_blank')}
                    onPrint={() => {
                      const printWindow = window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`, '_blank');
                      if (printWindow) printWindow.onload = () => printWindow.print();
                    }}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* POA Tab */}
        {activeTab === 'poa' && (
          <div className="dashboard-card">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Power of Attorney</h3>
                <p className="text-sm text-slate-500">Limited Power of Attorney status for this case</p>
              </div>
            </div>
            
            {poaData ? (
              <div className={`border rounded-lg p-6 ${
                poaData.status === 'completed' ? 'border-green-200 bg-green-50' :
                poaData.status === 'pending_admin' ? 'border-amber-200 bg-amber-50' :
                'border-slate-200 bg-slate-50'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <Scale size={20} className="text-purple-600" />
                      Limited Power of Attorney
                    </h4>
                    <p className="text-sm text-slate-600 mt-1">
                      {poaData.form_data?.merchant_legal_name || caseData?.company_name}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    poaData.status === 'completed' ? 'bg-green-100 text-green-700' :
                    poaData.status === 'pending_admin' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {poaData.status === 'completed' ? '✓ Fully Executed' :
                     poaData.status === 'pending_admin' ? '⏳ Pending Admin Signature' :
                     '📝 Draft'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-slate-500">State:</span>
                    <span className="ml-2 font-medium text-slate-900">{poaData.form_data?.state_of_incorporation || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Entity Type:</span>
                    <span className="ml-2 font-medium text-slate-900">{poaData.form_data?.entity_type || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Client Signed:</span>
                    <span className="ml-2 font-medium text-slate-900">
                      {poaData.client_signature?.signed_at 
                        ? format(new Date(poaData.client_signature.signed_at), 'MMM dd, yyyy')
                        : 'Not yet'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Admin Signed:</span>
                    <span className="ml-2 font-medium text-slate-900">
                      {poaData.admin_signature?.signed_at 
                        ? format(new Date(poaData.admin_signature.signed_at), 'MMM dd, yyyy')
                        : 'Pending'}
                    </span>
                  </div>
                </div>
                
                {poaData.status === 'completed' && (
                  <button
                    onClick={async () => {
                      try {
                        const response = await poaAPI.downloadPDF(poaData.id);
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `LPOA_${caseData?.company_name || 'document'}.pdf`;
                        link.click();
                        window.URL.revokeObjectURL(url);
                        toast.success('POA downloaded');
                      } catch (error) {
                        toast.error('Failed to download POA');
                      }
                    }}
                    className="primary-button flex items-center gap-2"
                  >
                    <Download size={18} />
                    Download Signed POA
                  </button>
                )}
              </div>
            ) : (
              <div className="text-center py-12 bg-slate-50 rounded-lg">
                <Scale className="mx-auto text-slate-400 mb-4" size={48} />
                <p className="text-slate-600 mb-2">No Power of Attorney on file</p>
                <p className="text-sm text-slate-400">A Limited POA is required to authorize Reserve Release AI to act on your behalf</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="dashboard-card">
            <CaseTaskBoard 
              caseId={id} 
              caseName={caseData?.company_name}
              clientId={caseData?.client_id}
              clientName={caseData?.client_name}
              isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
              onTaskUpdate={loadCaseData}
            />
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div>
            <MessageBoard caseId={id} messagesAPI={messagesAPI} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseDetail;
